import { Popover, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { ContactInfoList, Container, Flex, Nav } from '../../common';
import { NavbarPrimaryDesktopCtaButton } from './NavbarPrimaryDesktopCtaButton';
import { NavbarPrimaryDesktopItem } from './NavbarPrimaryDesktopItem';
import { NavbarPrimaryLogo } from './NavbarPrimaryLogo';
import { NavbarPrimaryMobileItem } from './NavbarPrimaryMobileItem';

/* ─── Desktop Components ────────────────────────── */
const DesktopNavListContainer = styled.div`
  ${tw`items-center justify-center hidden lg:flex`}
`;

const DesktopNavList = styled.ul`
  ${tw`flex space-x-8`}
`;

const DesktopNavCtaContainer = styled.div`
  ${tw`flex items-center justify-end`}
`;

/* ─── Mobile Styled Components ───────────────────── */
const MobileMenuLinks = styled.ul`
  li {
    cursor: pointer;
    color: ${(props) => props.$color};
    font-size: 1.125rem;
    font-weight: 600;
    transition: all 0.25s;
    &:hover {
      color: ${(props) => props.$colorHover};
    }
    svg {
      height: 1em;
      width: 1em;
    }
    a {
      color: ${(props) => props.$color};
      font-size: 1.125rem;
      font-weight: 600;
      transition: all 0.25s;
      &:hover {
        color: ${(props) => props.$colorHover};
      }
    }
  }
`;

const MobileNavCtaContainer = styled.div`
  background-color: ${(props) => props.$bgColor};
`;

const PopoverButton = styled((props) => <Popover.Button {...props} />)`
  color: ${(props) => props.$color};
  background: none;
  &:hover {
    color: ${(props) => props.$colorHover};
    background-color: ${(props) => props.$bgColorHover};
  }
`;

const XIcon = styled((props) => <XMarkIcon {...props} />)`
  color: ${(props) => props.$color};
  background: none;
  &:hover {
    color: ${(props) => props.$colorHover};
    background-color: ${(props) => props.$bgColorHover};
  }
`;

const BarsIcon = styled((props) => <Bars3Icon {...props} />)`
  color: ${(props) => props.$color};
  background: none;
  &:hover {
    color: ${(props) => props.$colorHover};
    background-color: ${(props) => props.$bgColorHover};
  }
`;

/* ─── NavbarPrimary Component ───────────────────── */
const NavbarPrimary = ({ page, sanity }) => {
  const { colors, navbars } = sanity;

  return (
    <Nav
      idName="nav-primary"
      bgColor={colors?.neutral?.white}
      textColor={colors?.neutral?.darker}
      padding="none"
      classes="w-full h-24"
    >
      <Container padding="none" classes="max-w-7xl m-auto px-4 lg:px-0 h-24">
        <Flex
          classes={`h-24 gap-x-2 items-center ${
            navbars?.hideMobileNav ? 'justify-center' : 'justify-between'
          }`}
        >
          {/* Logo Section */}
          <NavbarPrimaryLogo page={page} sanity={sanity} />

          {/* Desktop Navigation */}
          <DesktopNavListContainer>
            <DesktopNavList $color={colors?.neutral?.dark} $colorHover={colors?.accent?.default}>
              {navbars?.navItem?.map((nav, index) => (
                <NavbarPrimaryDesktopItem key={index} nav={nav} page={page} sanity={sanity} />
              ))}
            </DesktopNavList>
          </DesktopNavListContainer>

          {/* Desktop Call-to-Action */}
          <DesktopNavCtaContainer>
            <NavbarPrimaryDesktopCtaButton page={page} sanity={sanity} />
          </DesktopNavCtaContainer>

          {/* Mobile Navigation */}
          {!navbars?.hideMobileNav && (
            <Popover className="relative lg:hidden">
              {({ open }) => (
                <>
                  <PopoverButton
                    $color={colors?.primary?.dark}
                    $colorHover={colors?.primary?.dark}
                    $bgColor={colors?.neutral?.white}
                    $bgColorHover={colors?.neutral?.white}
                    className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  >
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XIcon
                        $color={colors?.accent?.default}
                        $colorHover={colors?.accent?.dark}
                        className="block w-10 h-10"
                        aria-hidden="true"
                      />
                    ) : (
                      <BarsIcon
                        $color={colors?.accent?.default}
                        $colorHover={colors?.accent?.dark}
                        className="block w-10 h-10"
                        aria-hidden="true"
                      />
                    )}
                  </PopoverButton>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 -translate-y-10"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute -right-4 top-[4.7rem] z-10 w-screen max-h-[75vh] transform overflow-y-auto shadow-2xl">
                      <div className="shadow-lg ring-black ring-1 ring-opacity-5">
                        <div className="bg-white p-7">
                          <MobileMenuLinks
                            $color={colors?.neutral?.darker}
                            $colorHover={colors?.accent?.default}
                            className="flex flex-col space-y-8"
                          >
                            {navbars?.navItem?.map((nav, index) => (
                              <NavbarPrimaryMobileItem key={index} nav={nav} sanity={sanity} />
                            ))}
                          </MobileMenuLinks>
                        </div>
                        <MobileNavCtaContainer
                          $bgColor={colors?.primary?.darker}
                          className="flex justify-center p-6"
                        >
                          <ContactInfoList page={page} sanity={sanity} />
                        </MobileNavCtaContainer>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          )}
        </Flex>
      </Container>
    </Nav>
  );
};

export { NavbarPrimary };
